import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { 
  LayoutDashboard, 
  Map as MapIcon, 
  FileCheck, 
  Users, 
  Globe, 
  LifeBuoy, 
  ShieldAlert, 
  Sliders,
  LogOut, 
  Menu, 
  X,
  User,
  Search,
  AlertTriangle,
  Car
} from 'lucide-react';

export default function Layout() {
  const token = localStorage.getItem('adminToken');
  const adminEmail = localStorage.getItem('adminEmail') || 'admin@safe.com';
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  const [activeEmergencies, setActiveEmergencies] = useState([]);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [pendingValidationCount, setPendingValidationCount] = useState(0);
  const [pendingPayoutCount, setPendingPayoutCount] = useState(0);
  const [pendingSubscriptionCount, setPendingSubscriptionCount] = useState(0);
  const [onlineDriversCount, setOnlineDriversCount] = useState(0);
  const [onlinePassengersCount, setOnlinePassengersCount] = useState(0);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
  };

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      // 1. Fetch active emergencies
      try {
        const response = await apiRequest('/api/admin/emergencies/active');
        if (response.ok) {
          const data = await response.json();
          setActiveEmergencies(data);
        }
      } catch (err) {
        console.error("Failed to fetch active emergencies:", err);
      }

      // 2. Fetch support tickets to compute unresolved count
      try {
        const response = await apiRequest('/api/admin/support/tickets');
        if (response.ok) {
          const data = await response.json();
          const count = data.filter(t => t.status !== 'RESOLVED').length;
          setOpenTicketsCount(count);
        }
      } catch (err) {
        console.error("Failed to fetch support tickets:", err);
      }

      // 3. Fetch pending validation documents grouped by driver
      try {
        const response = await apiRequest('/api/admin/documents');
        if (response.ok) {
          const data = await response.json();
          setPendingValidationCount(data.length);
        }
      } catch (err) {
        console.error("Failed to fetch pending validation documents:", err);
      }

      // 4. Fetch pending payout tickets
      try {
        const response = await apiRequest('/api/admin/tickets/status/PENDING');
        if (response.ok) {
          const data = await response.json();
          const count = data.filter(t => t.type === 'PAYOUT_REQUEST').length;
          setPendingPayoutCount(count);
        }
      } catch (err) {
        console.error("Failed to fetch pending payout tickets:", err);
      }

      // 5. Fetch pending subscription requests
      try {
        const response = await apiRequest('/api/admin/revenue-mode-requests?status=PENDING');
        if (response.ok) {
          const data = await response.json();
          setPendingSubscriptionCount((data.requests || []).length);
        }
      } catch (err) {
        console.error("Failed to fetch pending subscription requests:", err);
      }

      // 6. Fetch online counts
      try {
        const response = await apiRequest('/api/admin/users/online-counts');
        if (response.ok) {
          const data = await response.json();
          setOnlineDriversCount(data.onlineDrivers || 0);
          setOnlinePassengersCount(data.onlinePassengers || 0);
        }
      } catch (err) {
        console.error("Failed to fetch online counts:", err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // poll every 10 seconds
    return () => clearInterval(interval);
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const navItems = [
    { name: 'Tableau de bord', path: '/', icon: LayoutDashboard },
    { name: 'Gestion des Courses', path: '/rides', icon: Car },
    { name: 'Carte Chauffeurs', path: '/map', icon: MapIcon },
    { name: 'File de Validation', path: '/validation', icon: FileCheck },
    { name: 'Gestion Chauffeurs', path: '/drivers', icon: User },
    { name: 'Gestion Clients', path: '/users', icon: Users },
    { name: 'Gestion des Zones', path: '/zones', icon: Globe },
    { name: 'Demandes de Versements', path: '/payout-requests', icon: FileCheck },
    { name: 'Support', path: '/support', icon: LifeBuoy },
    { name: 'Demandes Abonnements', path: '/revenue-requests', icon: Users },
    { name: 'Configuration Tarifaire', path: '/pricing-config', icon: Sliders },
    { name: 'Logs d\'Audit', path: '/audit-logs', icon: ShieldAlert },
    { name: 'Alertes SOS', path: '/emergencies', icon: AlertTriangle },
  ];

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/login');
  };

  const getPageTitle = () => {
    const activeItem = navItems.find(item => item.path === location.pathname);
    if (activeItem) return activeItem.name;
    if (location.pathname.startsWith('/drivers/')) return 'Profil Chauffeur 360°';
    return 'Safe Administration';
  };

  const visibleEmergencies = activeEmergencies.filter(alert => !dismissedAlerts.includes(alert.id));

  return (
    <div style={styles.container}>
      {/* Mobile Backdrop Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)} 
          style={styles.mobileBackdrop}
        />
      )}

      {/* Sidebar */}
      <div style={{
        ...styles.sidebar,
        position: isMobile ? 'fixed' : 'relative',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: isMobile ? 100 : 10,
        width: isMobile ? '280px' : (sidebarOpen ? '260px' : '0px'),
        transform: isMobile 
          ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)')
          : 'none',
        opacity: (!isMobile && !sidebarOpen) ? 0 : 1,
        pointerEvents: (isMobile && !sidebarOpen) ? 'none' : 'auto',
        boxShadow: isMobile && sidebarOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none'
      }}>
        <div style={styles.logoContainer}>
          <div style={styles.logoBadge}>S</div>
          <span style={styles.logoText}>SAFE <span style={styles.logoSubtext}>Admin</span></span>
          {isMobile && (
            <button 
              onClick={() => setSidebarOpen(false)} 
              style={styles.closeSidebarBtn}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <nav style={styles.nav}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={() => {
                  if (isMobile) setSidebarOpen(false);
                }}
                style={{
                  ...styles.navLink,
                  ...(isActive ? styles.navLinkActive : {})
                }}
              >
                <Icon size={18} color={isActive ? '#0052ff' : '#5a5a56'} />
                <span style={styles.navText}>{item.name}</span>
                {item.path === '/support' && openTicketsCount > 0 && (
                  <span style={styles.badgeSupport}>{openTicketsCount}</span>
                )}
                {item.path === '/emergencies' && activeEmergencies.length > 0 && (
                  <span style={styles.badgeSos}>{activeEmergencies.length}</span>
                )}
                {item.path === '/validation' && pendingValidationCount > 0 && (
                  <span style={styles.badgeNeutral}>{pendingValidationCount}</span>
                )}
                {item.path === '/payout-requests' && pendingPayoutCount > 0 && (
                  <span style={styles.badgeNeutral}>{pendingPayoutCount}</span>
                )}
                {item.path === '/revenue-requests' && pendingSubscriptionCount > 0 && (
                  <span style={styles.badgeNeutral}>{pendingSubscriptionCount}</span>
                )}
                {item.path === '/drivers' && onlineDriversCount > 0 && (
                  <span style={styles.badgeOnline}>{onlineDriversCount}</span>
                )}
                {item.path === '/users' && onlinePassengersCount > 0 && (
                  <span style={styles.badgeOnline}>{onlinePassengersCount}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div style={styles.sidebarFooter}>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            <LogOut size={16} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={styles.mainArea}>
        {/* Header */}
        <header style={{
          ...styles.header,
          padding: isMobile ? '0 12px' : '0 24px',
          height: isMobile ? '56px' : '70px',
        }}>
          <div style={styles.headerLeft}>
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)} 
              style={styles.menuToggle}
              aria-label="Toggle Menu"
            >
              {sidebarOpen && !isMobile ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 style={{
              ...styles.pageTitle,
              fontSize: isMobile ? '0.95rem' : '1.1rem',
              maxWidth: isMobile ? '160px' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>{getPageTitle()}</h1>
          </div>

          {!isMobile && (
            <div style={styles.headerSearch}>
              <Search size={16} color="#94a3b8" style={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Rechercher chauffeur, ID, document..." 
                className="search-input"
              />
            </div>
          )}

          <div style={styles.headerRight}>
            <div style={styles.profileBadge}>
              <div style={styles.avatar}>
                <User size={16} color="#0052ff" />
              </div>
              {!isMobile && (
                <span style={styles.profileEmail}>{adminEmail}</span>
              )}
            </div>
          </div>
        </header>

        {/* Active Emergency Banner */}
        {visibleEmergencies && visibleEmergencies.length > 0 && (
          <div style={styles.emergencyBanner}>
            {visibleEmergencies.map((alert) => (
              <div key={alert.id} style={styles.emergencyItem}>
                <div style={styles.emergencyLeft}>
                  <div style={styles.emergencyPulse} />
                  <span style={styles.emergencyText}>
                    <strong>🚨 ALERTE SOS :</strong> Le passager <strong>{alert.passengerName}</strong> ({alert.passengerPhone || 'Pas de numéro'}) signale un danger ! Chauffeur : {alert.driverName || 'Non assigné'} • Véhicule : {alert.licensePlate || 'N/A'}
                  </span>
                </div>
                <div style={styles.emergencyRight}>
                  <a
                    href={`https://maps.google.com/?q=${alert.latitude},${alert.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.emergencyLink}
                  >
                    📍 Position GPS
                  </a>
                  <button
                    onClick={() => handleDismissAlert(alert.id)}
                    style={styles.dismissBtn}
                    title="Masquer cette bannière"
                  >
                    <X size={14} color="#ba1a1a" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Content Body */}
        <main style={{
          ...styles.contentBody,
          padding: isMobile ? '12px 8px' : '24px',
        }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles = {
  mobileBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(3px)',
    zIndex: 90,
  },
  closeSidebarBtn: {
    marginLeft: 'auto',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    backgroundColor: 'var(--bg-primary)',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--bg-secondary)',
    borderRight: '0.5px solid var(--border-color)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 10,
    overflow: 'hidden',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px',
    borderBottom: '0.5px solid var(--border-color)',
  },
  logoBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    fontFamily: 'var(--font-title)',
    fontWeight: '600',
    fontSize: '1.1rem',
  },
  logoText: {
    fontFamily: 'var(--font-title)',
    fontWeight: '600',
    fontSize: '1.1rem',
    color: 'var(--text-primary)',
    letterSpacing: '0.02em',
  },
  logoSubtext: {
    fontSize: '0.8rem',
    fontWeight: '400',
    color: 'var(--text-secondary)',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '20px 12px',
    flex: 1,
    overflowY: 'auto',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  navLinkActive: {
    backgroundColor: 'rgba(0, 82, 255, 0.05)',
    color: 'var(--primary)',
    borderLeft: '3px solid var(--primary)',
  },
  navText: {
    whiteSpace: 'nowrap',
  },
  badgeSupport: {
    marginLeft: 'auto',
    backgroundColor: '#0052ff',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center',
  },
  badgeSos: {
    marginLeft: 'auto',
    backgroundColor: '#ba1a1a',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center',
  },
  badgeNeutral: {
    marginLeft: 'auto',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    color: 'var(--text-secondary)',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center',
  },
  badgeOnline: {
    marginLeft: 'auto',
    backgroundColor: '#10b981',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center',
  },
  sidebarFooter: {
    padding: '20px 12px',
    borderTop: '0.5px solid var(--border-color)',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '0.5px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--danger)',
    fontFamily: 'var(--font-title)',
    fontWeight: '500',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  mainArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '70px',
    padding: '0 24px',
    backgroundColor: 'var(--bg-secondary)',
    borderBottom: '0.5px solid var(--border-color)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  menuToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
  },
  pageTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  headerSearch: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '320px',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    pointerEvents: 'none',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  profileBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 12px 6px 6px',
    borderRadius: '9999px',
    backgroundColor: 'var(--bg-primary)',
    border: '0.5px solid var(--border-color)',
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 82, 255, 0.08)',
    borderWidth: 0.5,
    borderColor: 'rgba(0, 82, 255, 0.15)',
  },
  profileEmail: {
    fontSize: '0.85rem',
    color: 'var(--text-primary)',
    fontWeight: '500',
  },
  contentBody: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
    backgroundColor: 'var(--bg-primary)',
  },
  emergencyBanner: {
    backgroundColor: '#fee2e2',
    borderBottom: '1px solid #fecaca',
    padding: '12px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    animation: 'pulseBg 2s infinite ease-in-out',
  },
  emergencyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  emergencyLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emergencyPulse: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ba1a1a',
    animation: 'pulseDot 1.2s infinite',
  },
  emergencyText: {
    color: '#ba1a1a',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  emergencyLink: {
    backgroundColor: '#ba1a1a',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: '600',
    textDecoration: 'none',
    boxShadow: '0 2px 4px rgba(186, 26, 26, 0.2)',
  },
  emergencyRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dismissBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(186, 26, 26, 0.1)',
    border: 'none',
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  }
};
